// FieldNotes.js
// Field Notes Class, holding the general work description and weather conditions. Will also be used to hold the activites section which will consist of a time and a description.
import { v4 as uuidv4 } from "uuid";

export class FieldNoteActivity {
  constructor(data = {}) {
    this.rowId = data.id || uuidv4();
    this.time = data.time || null;
    this.notes = data.notes || null;
    this.noteOrder = data.note_order ?? null;
  }

  // I know this looks weird, but without a function like this, the time will not update in the state for some reason, that is also why the other functions are like this as well.
  updateTime(time) {
    this.time = time;
  }

  updateNotes(notes) {
    this.notes = notes;
  }

  updateNoteOrder(noteOrder) {
    this.noteOrder = noteOrder;
  }

  // To json for sending to the database
  toJson() {
    return {
      id: this.rowId,
      time: this.time,
      notes: this.notes,
      note_order: this.noteOrder,
    };
  }

  toPatch(action, fields) {
    const fieldMap = {
      time: "time",
      notes: "notes",
      noteOrder: "note_order",
    };
    const patch = {
      patch_type: "field_notes_activity",
      action: action,
      id: this.rowId,
    };
    for (const [key, value] of Object.entries(fields)) {
      const dbField = fieldMap[key];
      if (dbField) {
        patch[dbField] = value;
      }
    }
    return patch;
  }
}

export class FieldNotes {
  constructor(data = {}) {
    this.workDescription = data.work_description || null;
    this.weather = data.weather || null;
    // Activities is an array that will hold FieldNoteActivity objects, if there are no activities, then create an array with 5 blank activities, have the note order be the index of the activity
    this.activities = data.activities
      ? data.activities.map((activity) => new FieldNoteActivity(activity))
      : Array.from(
          { length: 5 },
          (_, index) => new FieldNoteActivity({ note_order: index })
        );
  }

  updateWorkDescription(workDescription) {
    this.workDescription = workDescription;
  }

  updateWeather(weather) {
    this.weather = weather;
  }

  // Delete all activities from the activities array
  deleteAllActivities() {
    this.activities = [];
  }

  // Add a blank new activity to the activities array, with the note order being the index of the activity
  addBlankNewActivity() {
    this.activities.push(
      new FieldNoteActivity({ note_order: this.activities.length })
    );
  }

  // Reset the note order of the activities when a user drags and drops an activity
  resetNoteOrder(newActivities) {
    newActivities.forEach((newActivity, index) => {
      this.activities
        .find((activity) => activity.rowId === newActivity.rowId)
        .updateNoteOrder(index);
    });
    // Sort the activities by note order so they show up correctly after being dragged and dropped.
    this.activities = this.activities.sort((a, b) => a.noteOrder - b.noteOrder);
  }

  // Remove an activity from the activities array
  removeActivity(activityRowId) {
    this.activities = this.activities.filter(
      (activity) => activity.rowId !== activityRowId
    );
    // Updating the note order of the activities after the new activity, if the index is greater than the index of the new activity, then decrement the note order by 1
    this.activities.forEach((activity) => {
      const index = this.activities.indexOf(activity);
      if (activity.noteOrder !== index) {
        activity.noteOrder = index;
      }
    });
  }

  // Sorting the activities by note order
  sortActivities() {
    this.activities.sort((a, b) => a.noteOrder - b.noteOrder);
  }

  updateActivityTime(rowId, time) {
    const activity = this.activities.find(
      (activity) => activity.rowId === rowId
    );
    activity.updateTime(time);
  }

  updateActivityNotes(rowId, notes) {
    const activity = this.activities.find(
      (activity) => activity.rowId === rowId
    );
    activity.updateNotes(notes);
  }

  updateActivityNoteOrder(rowId, noteOrder) {
    const activity = this.activities.find(
      (activity) => activity.rowId === rowId
    );
    activity.updateNoteOrder(noteOrder);
  }

  // To json for sending to the database
  toJson() {
    return {
      work_description: this.workDescription,
      weather: this.weather,
      field_notes: this.activities.map((activity) => activity.toJson())
        ? this.activities.map((activity) => activity.toJson())
        : [],
    };
  }

  toPatch(action, fields) {
    const fieldMap = {
      workDescription: "work_description",
      weather: "weather",
    };
    const patch = {
      patch_type: "field_notes",
      action: action,
    };
    for (const [key, value] of Object.entries(fields)) {
      const dbField = fieldMap[key];
      if (dbField) {
        patch[dbField] = value;
      }
    }
    return patch;
  }
}
